// نشانیِ «مسیر بگیر».
//
// این‌جا هیچ اپی انتخاب نمی‌شه. ما فقط یه نشانی می‌سازیم و می‌دیمش دستِ
// سیستم‌عامل؛ اینکه گوگل‌مپ باز شه یا ویز یا اوبر یا نقشه‌ی خودِ گوشی،
// تصمیمِ گوشیه — نه تصمیمِ ما. برای همین هم لیستِ اپ‌ها جایی توی کد نیست:
// هر اپی که کاربر نصب کنه، خودبه‌خود جزو گزینه‌هاست.
//
// دو نشانیِ متفاوت لازمه چون دو سیستم‌عامل دو جورن:
//
// - اندروید: طرحِ `geo:` رو همه‌ی اپ‌های نقشه اعلام می‌کنن، پس اندروید
//   خودش لیستِ انتخاب رو نشون می‌ده. مبدأ پارامترِ جدا نداره — بخشِ اولِ
//   `geo:` مرکزِ نماست و مقصد توی `q` می‌شینه. اپِ نقشه هم مسیر رو از
//   همون‌جایی که هستی می‌گیره، پس نتیجه همونیه که کاربر می‌خواد.
//
// - iOS: لیستِ انتخابِ سیستمی وجود نداره؛ `maps://` یعنی نقشه‌ی خودِ
//   گوشی. این طرح برخلافِ `geo:` مبدأ (`saddr`) رو می‌فهمه.
//
// اگه هیچ‌کدوم باز نشد (گوشیِ بدونِ اپِ نقشه)، نسخه‌ی وب هست: نگاه کن به
// directionsWebUrl.
//
// عمداً هیچ چیزی از react-native وارد نمی‌شه تا `npm test` بتونه بخوندش —
// تصمیمِ پلتفرم به‌شکلِ ورودی می‌آد، نه از Platform.OS.

export interface Point { lat: number; lng: number }

const pair = (p: Point): string => `${p.lat},${p.lng}`;

/**
 * نشانیِ نیتیو برای مسیر تا `to`.
 *
 * `from` نال یعنی هنوز موقعیتِ کاربر رو نداریم — اون‌وقت فقط مقصد فرستاده
 * می‌شه و اپِ نقشه خودش از موقعیتِ فعلی شروع می‌کنه.
 */
export function directionsUrl(
  to: Point, from: Point | null, label: string | null, os: string,
): string {
  if (os === 'ios') {
    const saddr = from ? `saddr=${pair(from)}&` : '';
    return `maps://?${saddr}daddr=${pair(to)}`;
  }
  // پرانتزِ اسم بخشی از خودِ طرحِ geo است؛ اسم باید امن شه وگرنه یه «&»
  // توی عنوان بقیه‌ی نشانی رو می‌بلعه.
  const name = label ? `(${encodeURIComponent(label)})` : '';
  return `geo:${pair(from ?? to)}?q=${pair(to)}${name}`;
}

/** پناهِ آخر: نه اپی، نه طرحی — همون مسیر روی وب. */
export function directionsWebUrl(to: Point, from: Point | null): string {
  const origin = from ? `&origin=${pair(from)}` : '';
  return `https://www.google.com/maps/dir/?api=1&destination=${pair(to)}${origin}`;
}

/**
 * مسیر تا جایی که فقط **اسم و نشانی**اش را داریم و نه مختصاتش.
 *
 * صفحه‌ی مهمان دقیقاً همین حالت است: «کجا» آن‌جا یک رشته‌ی متنی‌ست — یا
 * `place_name`ِ دست‌نوشته‌ی میزبان («خونه‌ی آنی»)، یا اسمِ محل و نشانیِ
 * رخدادِ کاتالوگ. هیچ‌کدام از این دو دریچه lat/lng بیرون نمی‌دهند، پس
 * `directionsUrl` بالا (که نقطه می‌خواهد) اصلاً قابلِ استفاده نیست.
 *
 * جستجوی متنی جوابِ درستِ همین حالت است و نه یک عقب‌نشینی: گوگل‌مپ خودش
 * نشانی را پیدا می‌کند، و برای «خونه‌ی آنی» هم دستِ‌کم چیزی برای جستجو
 * می‌گذارد جلوی کاربر به‌جای یک نقطه‌ی اشتباه.
 *
 * روی اندروید همین نشانی اگر اپِ گوگل‌مپ نصب باشد خودِ اپ را باز می‌کند —
 * پس دکمه روی گوشی همان کاری را می‌کند که ازش انتظار می‌رود.
 */
export const directionsSearchUrl = (query: string): string =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
